import React from "react";
import i18n from "i18n";
import { getFormattedDate } from "components/Messages/utils";
import { useReports } from "hooks/useReports";
import useFilterOptionsUtils from "components/MultiSelect/filterOptionsUtils";
import {
  DecisionsTypeArray,
  getReportStatusLabel,
  StatusesEnums,
  UserRole,
} from "components/Reports/reportAssets/assets";
import { tableColumns, typeOfFilter } from "./types";

const getBooleanString = (value: boolean): string => {
  return value ? i18n.t("yes") : i18n.t("no");
};
const getPercentString = (value: string): string => {
  return `${value}%`;
};

export const GetSelectOptionsByType = () => {
  const { rejectionReasons, reportsViolationsTypes, tags } = useReports();

  const { enumOptions } = useFilterOptionsUtils();

  const selectOptions = {
    [typeOfFilter.statusSelect]: Object.values(StatusesEnums).map(
      (enumItem) => {
        return {
          // D-08: localized status DISPLAY label via the enum lookup helper.
          // This ControlCenter analytics surface is CONTROLCENTER-only (see
          // AppRouter `/control-center/reports/*`), so the role is fixed.
          // `value` keeps the stable id used for matching/filtering.
          label: getReportStatusLabel(enumItem.id, UserRole.CONTROLCENTER),
          value: enumItem.id,
        };
      }
    ),
    [typeOfFilter.offenceSelect]: enumOptions(reportsViolationsTypes),
    [typeOfFilter.tagsSelect]: enumOptions(tags?.tagList),
    [typeOfFilter.decisionSelect]: enumOptions(DecisionsTypeArray),
    [typeOfFilter.rejectionReasonSelect]: enumOptions(rejectionReasons),
  };
  return selectOptions;
};

// const useTagName = (tagIds: string[]) => {
//   const { tags } = useReports();
//   const matchingTags = tags?.tagList?.filter((tag) =>
//     tagIds.includes(tag.tagCode)
//   );

//   const tagNames = matchingTags.map((tag) => tag.tagName).join(", ");
//   return tagNames;
// };

const useReportStatusName = (statusId: string) => {
  const { getAllStatuses } = useReports();
  const statuses = getAllStatuses();
  const status = statuses?.find((s) => s.id === statusId);
  // D-08: localize the finite status DISPLAY label via the lookup helper, keyed
  // by the stable status id. CONTROLCENTER-only analytics surface, so the role is
  // fixed (see AppRouter `/control-center/reports/*`). Falls back to the raw
  // server `type` when the id is unknown.
  return status
    ? getReportStatusLabel(status.id, UserRole.CONTROLCENTER)
    : status?.type;
};

export const getVolunteerTableColumns: tableColumns[] = [
  {
    Header: i18n.t("uuidVolunteer"),
    accessor: "uuid",
  },
  {
    Header: i18n.t("tz"),
    accessor: "tz",
  },
  {
    Header: i18n.t("fullName"),
    accessor: "fullName",
  },
  {
    Header: i18n.t("email"),
    accessor: "email",
  },
  {
    Header: i18n.t("phone"),
    accessor: "phoneNumber",
  },
  {
    Header: i18n.t("isRegistered"),
    accessor: "isRegistered",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("isVerified"),
    accessor: "isVerifyByMimshalZamin",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("reportsGeneratedAmount"),
    accessor: "reportsAmount",
  },
  {
    Header: i18n.t("rejectionsAmount"),
    accessor: "disqualifiedReportsAmount",
  },
  {
    Header: i18n.t("lastReportUpdate"),
    accessor: "lastReportDate",
    type: typeOfFilter.date,
    Cell: ({ cell }: { cell: { value: string } }) => (
      <span>{getFormattedDate(cell.value)}</span>
    ),
  },
  {
    Header: i18n.t("numOfVersion"),
    accessor: "currentVersion",
  },
  {
    Header: i18n.t("updateAppDate"),
    accessor: "appUpdateDate",
    type: typeOfFilter.date,
    Cell: ({ cell }: { cell: { value: string } }) => (
      <span>{getFormattedDate(cell.value)}</span>
    ),
  },
  {
    Header: i18n.t("registrationDate"),
    accessor: "downloadTime",
    type: typeOfFilter.date,
    Cell: ({ cell }: { cell: { value: string } }) => (
      <span>{getFormattedDate(cell.value)}</span>
    ),
  },
];

export const getProductsTableColumns: tableColumns[] = [
  {
    Header: i18n.t("labelReportNumber"),
    accessor: "reportID",
  },
  {
    Header: i18n.t("labelDate"),
    accessor: "createdOn",
    type: typeOfFilter.date,
    Cell: ({ cell }: { cell: { value: string } }) => <span>{cell.value}</span>,
  },
  {
    Header: i18n.t("coordinates"),
    accessor: "coordinates",
  },
  {
    Header: i18n.t("city"),
    accessor: "cityName",
  },
  {
    Header: i18n.t("street"),
    accessor: "streetName",
  },
  {
    Header: i18n.t("labelRoad"),
    accessor: "reportRoad",
  },
  {
    Header: i18n.t("labelKm"),
    accessor: "reportKm",
  },
  {
    Header: i18n.t("status"),
    accessor: "reportStatusId",
    Cell: ({ cell }: { cell: { value: string } }) => (
      <span>{useReportStatusName(cell.value)}</span>
    ),
    type: typeOfFilter.statusSelect,
  },
  {
    Header: i18n.t("lastUpdateDate"),
    accessor: "updateOn",
    type: typeOfFilter.date,
    Cell: ({ cell }: { cell: { value: string } }) => <span>{cell.value}</span>,
  },
  {
    Header: i18n.t("controllerOffenceType"),
    accessor: "offenceTypeName",
    type: typeOfFilter.offenceSelect,
  },
  {
    Header: i18n.t("uuidVolunteer"),
    accessor: "volunteerUUID",
  },
  {
    Header: i18n.t("tz"),
    accessor: "volunteerTZ",
  },
  {
    Header: i18n.t("fullName"),
    accessor: "volunteerFullName",
  },
  {
    Header: i18n.t("controllerLicenseNumber"),
    accessor: "controllersLicenseNumber",
  },
  {
    Header: i18n.t("volunteerLicenseNumber"),
    accessor: "volunteerLicenseNumber",
  },
  {
    Header: i18n.t("modelType"),
    accessor: "carModel",
  },
  {
    Header: i18n.t("carColor"),
    accessor: "carColor",
  },
  {
    Header: i18n.t("isVideoWatched"),
    accessor: "videoHasSeen",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("commentToSeniorController"),
    accessor: "commentToSeniorContoller",
  },
  {
    Header: i18n.t("commentToPolice"),
    accessor: "commentToPolice",
  },
  {
    Header: i18n.t("isLicenseNumberUnclear"),
    accessor: "licenseNumberUnclear",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("isDataDoesNotMatch"),
    accessor: "dataDoesNotMatch",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("isMissingDetails"),
    accessor: "missingVolunteerDetails",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("isRoadMarkUnclear"),
    accessor: "roadMarkUnclear",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("tags"),
    accessor: "tagsList",
    Cell: ({ cell }: { cell: { value: boolean } }) => <span>{cell.value}</span>,
    type: typeOfFilter.tagsSelect,
  },
  {
    Header: i18n.t("reportDecision"),
    accessor: "decisionTypeName",
    type: typeOfFilter.decisionSelect,
  },
  {
    Header: i18n.t("rejectionReportReason"),
    accessor: "rejectReasonCode",
    type: typeOfFilter.rejectionReasonSelect,
  },
  {
    Header: i18n.t("isForImmediateTreatment"),
    accessor: "reportForImmediateTreatment",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("isForInformationalMaterial"),
    accessor: "recommendForInformationalMaterial",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("withdrawnByControlCenter"),
    accessor: "withdrawnByControlCenter",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("labelVolunteerSendDate"),
    accessor: "volunteerSendDate",
    type: typeOfFilter.date,
    Cell: ({ cell }: { cell: { value: string } }) => <span>{cell.value}</span>,
  },
];

export const getControllersTableColumns = (
  onCellClick: (controllerNumberId: any, fullName: string) => void
): tableColumns[] => {
  return [
    {
      Header: i18n.t("controllerNumberId"),
      accessor: "controllerId",
    },
    {
      Header: i18n.t("fullName"),
      accessor: "fullName",
    },
    {
      Header: i18n.t("percentReverseDecision"),
      accessor: "decisionPercentage",
      Cell: ({ cell }: { cell: { value: string } }) => (
        <span>{getPercentString(cell.value)}</span>
      ),
    },
    {
      Header: i18n.t("percentRejectionReports"),
      accessor: "canceledPercentage",
      Cell: ({ cell }: { cell: { value: string } }) => (
        <span>{getPercentString(cell.value)}</span>
      ),
    },
    {
      Header: i18n.t("percentSentToPoliceReports"),
      accessor: "policeDecisionPercentage",
      Cell: ({ cell }: { cell: { value: string } }) => (
        <span>{getPercentString(cell.value)}</span>
      ),
    },
    {
      Header: i18n.t("percentSentLetterReports"),
      accessor: "letterPercentage",
      Cell: ({ cell }: { cell: { value: string } }) => (
        <span>{getPercentString(cell.value)}</span>
      ),
    },
    {
      Header: i18n.t("reportsThatCompleted"),
      accessor: "totalReportsHendle",
      Cell: ({ cell }: { cell: { row: any; value: number } }) => (
        <button
          type="button"
          className="extra-data-button"
          onClick={() =>
            onCellClick(cell.row.values.controllerId, cell.row.values.fullName)
          }
        >
          {cell.value}
        </button>
      ),
    },
  ];
};

export const getControllersExtraDataTableColumns: tableColumns[] = [
  {
    Header: i18n.t("labelReportNumber"),
    accessor: "reportID",
  },
  {
    Header: i18n.t("reportDecision"),
    accessor: "reportDecisionName",
    type: typeOfFilter.decisionSelect,
  },
  {
    Header: i18n.t("isControllerReverseDecision"),
    accessor: "controllerChangedDecision",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
  {
    Header: i18n.t("isPoliceReverseDecision"),
    accessor: "policeReversedDecision",
    Cell: ({ cell }: { cell: { value: boolean } }) => (
      <span>{getBooleanString(cell.value)}</span>
    ),
    type: typeOfFilter.indication,
  },
];

export const getCarsTableColumns = (
  onCellClick: (licenseNumber: string) => void
): tableColumns[] => {
  return [
    {
      Header: i18n.t("licenseNumber"),
      accessor: "licenseNumber",
    },
    {
      Header: i18n.t("reportCount"),
      accessor: "totalReports",
      Cell: ({ cell }: { cell: { row: any; value: number } }) => (
        <button
          type="button"
          className="extra-data-button"
          onClick={() => onCellClick(cell.row.values.licenseNumber)}
        >
          {cell.value}
        </button>
      ),
    },
  ];
};

export const getCarExtraDataTableColumns: tableColumns[] = [
  {
    Header: i18n.t("labelReportNumber"),
    accessor: "reportId",
  },
  {
    Header: i18n.t("coordinates"),
    accessor: "coordinates",
  },
  {
    Header: i18n.t("city"),
    accessor: "cityName",
  },
  {
    Header: i18n.t("street"),
    accessor: "streetName",
  },
  {
    Header: i18n.t("labelRoad"),
    accessor: "reportRoad",
  },
  {
    Header: i18n.t("labelKm"),
    accessor: "reportKm",
  },
  {
    Header: i18n.t("uuidVolunteer"),
    accessor: "uuidUser",
  },
  {
    Header: i18n.t("tz"),
    accessor: "tzUserApp",
  },
  {
    Header: i18n.t("fullName"),
    accessor: "userAppFullName",
  },
  {
    Header: i18n.t("controllerOffenceType"),
    accessor: "offenceTypeName",
    type: typeOfFilter.offenceSelect,
  },
  {
    Header: i18n.t("reportDecision"),
    accessor: "decisionTypeName",
    type: typeOfFilter.decisionSelect,
  },
];
